// Pure, framework-free helpers for the retention-offer-limit gating logic
// used by the Retention icon in the Payment grid. Kept in sync with the
// equivalent module in racpad_agreement (utils/retentionOfferLimitUtils.ts)
// since the business rules are identical across all retention-icon surfaces.

// Backend now returns the raw "agreementActivities" ref_code array (the
// retention-offer activity types recorded across the agreement's exchange
// chain) instead of a pre-computed count; derive the count client-side.
export const getRetentionOfferCount = (
  agreementActivities: string[] | null | undefined
): number => {
  return Array.isArray(agreementActivities) ? agreementActivities.length : 0;
};

export interface RetentionOfferLimitConfig {
  retentionOfferLimitEnabled: boolean;
  maxRetentionOffersPerAgreement: number;
}

export interface RetentionOfferLimitState extends RetentionOfferLimitConfig {
  retentionOfferCount: number;
}

// Icon should not render at all when the store has the limit feature enabled
// but has configured a max of 0 (i.e. retention offers are fully disabled).
export const isRetentionIconHidden = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
}: RetentionOfferLimitConfig): boolean => {
  return retentionOfferLimitEnabled && maxRetentionOffersPerAgreement === 0;
};

// Icon should render but be disabled (blurred/no-op click) once the
// agreement's existing retention-offer count has reached the configured max.
export const isRetentionLimitReached = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
  retentionOfferCount,
}: RetentionOfferLimitState): boolean => {
  return (
    retentionOfferLimitEnabled &&
    maxRetentionOffersPerAgreement > 0 &&
    retentionOfferCount >= maxRetentionOffersPerAgreement
  );
};

export const getRetentionLimitTooltip = (
  maxRetentionOffersPerAgreement: number
): string => {
  return maxRetentionOffersPerAgreement === 1
    ? 'Retention offer has already been applied to this agreement. Only 1 offer is allowed per agreement.'
    : `Maximum retention offers (${maxRetentionOffersPerAgreement}) have been applied to this agreement.`;
};

// Derives the configured max from a raw (string) feature-flag value,
// matching the parsing rules already used inline in PaymentGrid.tsx:
// non-negative integers are honored, anything else falls back to 1.
export const getMaxRetentionOffersPerAgreement = (rawMax: any): number => {
  // Number('') and Number('   ') both coerce to 0, which would otherwise be
  // accepted as a valid non-negative integer and hide the icon. Treat blank
  // strings as "not configured" so they fall back to the default of 1.
  if (typeof rawMax === 'string' && rawMax.trim() === '') {
    return 1;
  }
  const configuredRetentionLimit = Number(rawMax);
  return Number.isInteger(configuredRetentionLimit) &&
    configuredRetentionLimit >= 0
    ? configuredRetentionLimit
    : 1;
};

// Matches the inline parsing rule in PaymentGrid.tsx: the flag is
// considered enabled unless the raw feature-flag value is exactly the
// string '1'.
export const getRetentionOfferLimitEnabled = (rawEnabled: any): boolean => {
  return rawEnabled === '1';
};

export interface RetentionOfferLimitData {
  retentionIconHidden: boolean;
  retentionLimitReached: boolean;
  retentionLimitTooltip: string;
}

// Single entry point for the Retention icon: combines the helpers above
// into the one shape the UI needs. The individual helpers stay exported
// for direct reuse/unit testing.
export const getRetentionOfferLimitData = (
  config: RetentionOfferLimitConfig,
  agreementActivities: string[] | null | undefined
): RetentionOfferLimitData => {
  const retentionOfferCount = getRetentionOfferCount(agreementActivities);

  return {
    retentionIconHidden: isRetentionIconHidden(config),
    retentionLimitReached: isRetentionLimitReached({
      ...config,
      retentionOfferCount,
    }),
    retentionLimitTooltip: getRetentionLimitTooltip(
      config.maxRetentionOffersPerAgreement
    ),
  };
};
